import React from 'react';
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  Stack,
  Fade
} from '@mui/material';
import {
  HomeOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';

/**
 * ErrorPage Component
 * 
 * A beautiful, user-friendly error page designed for end users.
 * Displays a professional error message with recovery options.
 * 
 * Features:
 * - Beautiful gradient background matching the admin theme
 * - Professional French messaging for clients
 * - Single recovery option: return to dashboard
 * - No technical details exposed to end users
 * - Responsive design that works on all devices
 */
export default function ErrorPage({ error, onReset }) {
  const handleGoHome = () => {
    // Reset the error boundary state
    if (onReset) {
      onReset();
    }
    // Navigate to dashboard
    window.location.href = '/dashboard/default';
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)
          `,
          zIndex: 0
        }}
      />

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Fade in timeout={800}>
          <Paper
            elevation={8}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 4,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Stack spacing={4} alignItems="center">
              {/* Error Icon */}
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  boxShadow: '0 10px 30px rgba(255, 107, 107, 0.3)',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': {
                      transform: 'scale(1)',
                      boxShadow: '0 10px 30px rgba(255, 107, 107, 0.3)'
                    },
                    '50%': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 15px 40px rgba(255, 107, 107, 0.4)'
                    },
                    '100%': {
                      transform: 'scale(1)',
                      boxShadow: '0 10px 30px rgba(255, 107, 107, 0.3)'
                    }
                  }
                }}
              >
                <ExclamationCircleOutlined style={{ fontSize: 60, color: 'white' }} />
              </Box>

              {/* Error Title */}
              <Typography 
                variant="h3" 
                component="h1" 
                fontWeight="bold" 
                sx={{ 
                  color: '#2c3e50',
                  mb: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Oups ! Une erreur s'est produite
              </Typography>

              {/* Error Message */}
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#7f8c8d', 
                  maxWidth: 500,
                  lineHeight: 1.6,
                  fontWeight: 400
                }}
              >
                Nous sommes désolés, mais quelque chose s'est mal passé. 
                Notre équipe technique a été automatiquement notifiée et travaille à résoudre le problème.
              </Typography>

              {/* Action Button */}
              <Box sx={{ mt: 4, width: '100%', maxWidth: 400 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<HomeOutlined />}
                  onClick={handleGoHome}
                  sx={{
                    px: 4,
                    py: 2,
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 35px rgba(102, 126, 234, 0.4)'
                    },
                    transition: 'all 0.3s ease',
                    width: '100%'
                  }}
                >
                  Retour au tableau de bord
                </Button>
              </Box>

              {/* Help Section */}
              <Box 
                sx={{ 
                  mt: 4, 
                  p: 3, 
                  backgroundColor: 'rgba(102, 126, 234, 0.05)', 
                  borderRadius: 3,
                  border: '1px solid rgba(102, 126, 234, 0.1)',
                  width: '100%'
                }}
              >
                <Typography variant="body1" sx={{ color: '#7f8c8d', fontWeight: 500 }}>
                  💡 <strong>Conseil :</strong> Retournez au tableau de bord pour continuer votre travail. 
                  Si le problème persiste, contactez notre support technique.
                </Typography>
              </Box>

              {/* Footer */}
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#bdc3c7',
                  mt: 2,
                  fontSize: '0.9rem'
                }}
              >
                Foire au Livre - Système de Gestion
              </Typography>
            </Stack>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
}
